import dayjs from 'dayjs';

/** Seconds → decimal hours (2 dp) */
export function secondsToHours(secs: number): number {
  return Math.round((secs / 3600) * 100) / 100;
}

/** Seconds → "HH:MM:SS" */
export function secondsToHMS(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** "2026-06-01" → "Jun 1", "2026-06" → "Jun 2026" */
export function formatBarLabel(key: string): string {
  if (/^\d{4}-\d{2}$/.test(key)) return dayjs(key + '-01').format('MMM YYYY');
  return dayjs(key).format('MMM D');
}

/** "2026-06-01" → "Mon, Jun 1" — used in tooltips and modals */
export function formatBarLabelWithDay(key: string): string {
  if (/^\d{4}-\d{2}$/.test(key)) return dayjs(key + '-01').format('MMM YYYY');
  return dayjs(key).format('ddd, MMM D');
}

/** Y-axis tick: show hours as "Xh" */
export function formatYAxisTick(value: number): string {
  return `${value}h`;
}

/** Given start/end date strings, return 'day' or 'month' granularity.
 *  Use month when range > 31 days (so we never exceed 31 bars). */
export function resolveChartGranularity(
  startDate: string,
  endDate: string,
): 'day' | 'month' {
  const diff = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
  return diff > 31 ? 'month' : 'day';
}

/** Map granularity → groupBy param string for the chart API call */
export function granularityToGroupBy(
  granularity: 'day' | 'month',
  extraField?: string,
): string {
  const base = granularity === 'day' ? 'Date' : 'Month';
  return extraField ? `${base},${extraField}` : base;
}

/**
 * Generate every date (or month) key in [startDate, endDate] at the given granularity.
 * Day keys:   "YYYY-MM-DD"
 * Month keys: "YYYY-MM"
 */
export function generateDateKeys(
  startDate: string,
  endDate: string,
  granularity: 'day' | 'month',
): string[] {
  const keys: string[] = [];
  let cursor = dayjs(startDate).startOf(granularity);
  const end = dayjs(endDate).startOf(granularity);
  while (!cursor.isAfter(end)) {
    keys.push(granularity === 'day' ? cursor.format('YYYY-MM-DD') : cursor.format('YYYY-MM'));
    cursor = cursor.add(1, granularity);
  }
  return keys;
}

/** Compute bar pixel width so bars remain clearly visible regardless of data density */
export function computeBarSize(dataLength: number): number {
  if (dataLength <= 5) return 56;
  if (dataLength <= 7) return 48;
  if (dataLength <= 10) return 40;
  if (dataLength <= 14) return 32;
  if (dataLength <= 21) return 24;
  if (dataLength <= 31) return 18;
  return 12;
}

export const BILLABLE_COLOR = '#5147D4';
export const NON_BILLABLE_COLOR = '#C4BEF0';
export const FALLBACK_PROJECT_COLOR = '#9e9e9e';
